<template>
  <v-modal
    id="show-case-logs-modal"
    class-name="modal-fullscreen-sm-down w-auto"
    size="lg"
    @closed="emit('closed')"
  >
    <modal-header>
      <modal-title class="d-flex align-items-center">
        <v-icon icon="history" size="sm" class="me-8 text-primary" />
        {{ $t("tourClient.caseLogs") }}
        <span v-if="logs.length" class="ms-8 badge bg-light text-muted fw-bold">{{ logs.length }}</span>
      </modal-title>
    </modal-header>

    <modal-body>
      <div v-if="logs && logs.length > 0" class="case-logs-container custom-scrollbar">
        <div v-for="(log, index) in logs" :key="log.id" class="log-item d-flex gap-12">
          <div class="log-timeline-wrapper d-flex flex-column align-items-center">
             <div class="log-icon" :class="getLogClass(log.type)">
                <v-icon :icon="getLogIcon(log.type)" size="xs" />
             </div>
             <div v-if="index !== logs.length - 1" class="log-timeline-line"></div>
          </div>
          <div class="log-content flex-grow-1 pb-16">
            <div class="d-flex justify-content-between align-items-start gap-8 mb-2">
               <div class="d-flex align-items-center gap-6 flex-wrap">
                  <span class="log-badge" :class="getLogClass(log.type)">{{ formatLogType(log.type) }}</span>
                  <span class="text-sm fw-bold text-dark">{{ log.message }}</span>
               </div>
               <span v-if="log.user" class="text-xs fw-semibold text-primary px-8 py-2 bg-light rounded-pill border whitespace-nowrap">{{ log.user }}</span>
            </div>
            <div class="d-flex align-items-center gap-8 mb-4">
               <span class="text-xs text-muted">{{ log.created_at }}</span>
            </div>
            <div v-if="log.details && Object.keys(log.details).length > 0" class="log-details-inline">
               <span v-for="(value, key) in log.details" :key="key" class="log-detail-chip">
                  <span class="fw-bold text-capitalize">{{ key.replace('_', ' ') }}:</span>
                  <span class="text-muted">{{ value }}</span>
               </span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-48 bg-light rounded-sm border-dashed">
        <v-icon icon="history" size="3xl" class="text-muted mb-12 opacity-25" />
        <h6 class="text-muted fw-bold mb-4">{{ $t("tourClient.noLogsFound") }}</h6>
        <p class="text-xs text-muted mb-0">Activities will appear here once the case starts moving.</p>
      </div>
    </modal-body>
    <modal-footer class="d-flex justify-content-end mt-4 px-20">
      <v-button class="btn-sm btn-primary" data-bs-dismiss="modal">
        {{ $t("general.button.close") }}
      </v-button>
    </modal-footer>
  </v-modal>
</template>

<script setup>
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";

const emit = defineEmits(["closed"]);
const props = defineProps({
  logs: {
    type: Array,
    default: () => [],
  },
});

const getLogIcon = (type) => {
  switch (type) {
    case 'case_created':
    case 'precase_created':
      return 'plus';
    case 'precase_new_version':
      return 'history';
    case 'booking_request_sent':
      return 'send';
    case 'client_email_sent':
      return 'mail';
    case 'quote_created':
      return 'bookmark';
    case 'services_paid':
      return 'check';
    case 'quote_accepted':
      return 'check-circle';
    default:
      return 'info';
  }
};

const getLogClass = (type) => {
  switch (type) {
    case 'services_paid':
    case 'quote_accepted':
      return 'log-success';
    case 'booking_request_sent':
    case 'client_email_sent':
      return 'log-info';
    case 'case_created':
    case 'precase_created':
      return 'log-primary';
    case 'quote_created':
      return 'log-quote';
    case 'precase_new_version':
      return 'log-warning';
    default:
      return 'log-neutral';
  }
};

const formatLogType = (type) => {
  if (!type) return '';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
</script>

<style lang="scss" scoped>
.case-logs-container {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;
  padding-left: 2px;
}

.log-timeline-wrapper {
  width: 28px;
  flex-shrink: 0;
}

.log-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 2;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.log-timeline-line {
  width: 2px;
  flex-grow: 1;
  background-color: #f1f3f5;
  margin: 2px 0;
}

.log-primary { background-color: #fd0c6f; color: #ffffff; }
.log-success { background-color: #57C68A; color: #ffffff; }
.log-info { background-color: #e6b328; color: #ffffff; }
.log-warning { background-color: #6c757d; color: #ffffff; }
.log-quote { background-color: #8b5cf6; color: #ffffff; }
.log-neutral { background-color: #e9ecef; color: #495057; }

.log-badge {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.log-badge.log-primary { background-color: rgba(253,12,111,0.12); color: #fd0c6f; }
.log-badge.log-success { background-color: rgba(87,198,138,0.15); color: #3a9d6a; }
.log-badge.log-info { background-color: rgba(230,179,40,0.15); color: #b8920f; }
.log-badge.log-warning { background-color: rgba(108,117,125,0.15); color: #495057; }
.log-badge.log-quote { background-color: rgba(139,92,246,0.15); color: #7c3aed; }
.log-badge.log-neutral { background-color: #f1f3f5; color: #6c757d; }

.log-details-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.log-detail-chip {
  font-size: 0.7rem;
  background: #f8f9fa;
  border: 1px solid #f1f3f5;
  border-radius: 4px;
  padding: 2px 8px;
  display: inline-flex;
  gap: 4px;
}

.whitespace-nowrap { white-space: nowrap; }
.border-dashed { border-style: dashed !important; }

.custom-scrollbar {
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: #f8f9fa; }
  &::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
  &::-webkit-scrollbar-thumb:hover { background: #ced4da; }
}
</style>
